import * as React from "react";
import { Loader2, Save } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type {
  LearnerAppConfigUpdateRequest,
  LearnerAppPaymentOwner,
  LearnerAppPricingMode,
} from "@eduviaos/shared";
import {
  formatLearnerAppPrice,
  useLearnerAppConfig,
  useUpdateLearnerAppConfig,
} from "@/lib/learnerAppConfig/api";

const PRICING_MODES: ReadonlyArray<{
  value: LearnerAppPricingMode;
  label: string;
  description: string;
}> = [
  {
    value: "bundled",
    label: "Bundled with seat",
    description: "Learning app access is included in the seat fee. No separate charge.",
  },
  {
    value: "separate",
    label: "Separate pricing",
    description:
      "Centre sets a monthly learner-app fee. Staff grant access after the learner pays at the desk.",
  },
  {
    value: "sa_push",
    label: "SA-pushed pricing",
    description:
      "Platform pushes a recommended price; centre still grants access manually after payment.",
  },
];

function rupeesToPaise(input: string): number {
  const parsed = Number.parseFloat(input.replace(/,/g, "").trim());
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

function paiseToRupeeField(paise: number): string {
  if (paise <= 0) return "";
  return String(paise / 100);
}

export function LearnerAppConfigPanel({ tenantSlug }: { tenantSlug: string }) {
  const configQuery = useLearnerAppConfig(tenantSlug);
  const saveMutation = useUpdateLearnerAppConfig(tenantSlug);

  const [pricingMode, setPricingMode] = React.useState<LearnerAppPricingMode>("bundled");
  const [monthlyRupees, setMonthlyRupees] = React.useState("");
  const [commissionPct, setCommissionPct] = React.useState("0");
  const [dirty, setDirty] = React.useState(false);
  const [paymentOwner, setPaymentOwner] = React.useState<LearnerAppPaymentOwner>("eduviaos");
  const [centreKeyId, setCentreKeyId] = React.useState("");
  const [centreKeySecret, setCentreKeySecret] = React.useState("");
  const [centreWebhookSecret, setCentreWebhookSecret] = React.useState("");
  const [paymentDirty, setPaymentDirty] = React.useState(false);

  React.useEffect(() => {
    if (!configQuery.data) return;
    setPricingMode(configQuery.data.pricing_mode);
    setMonthlyRupees(paiseToRupeeField(configQuery.data.monthly_price_paise));
    setCommissionPct(String(configQuery.data.commission_bps / 100));
    setPaymentOwner(configQuery.data.payment_owner);
    setCentreKeyId(configQuery.data.razorpay_key_id);
    setCentreKeySecret("");
    setCentreWebhookSecret("");
    setDirty(false);
    setPaymentDirty(false);
  }, [configQuery.data]);

  const monthlyPaise = rupeesToPaise(monthlyRupees);
  const commissionBps = Math.min(
    10_000,
    Math.max(0, Math.round(Number.parseFloat(commissionPct || "0") * 100)),
  );

  const body: LearnerAppConfigUpdateRequest = {
    pricing_mode: pricingMode,
    monthly_price_paise: pricingMode === "bundled" ? 0 : monthlyPaise,
    commission_bps: pricingMode === "separate" ? commissionBps : 0,
  };

  const canSave =
    dirty &&
    !saveMutation.isPending &&
    (pricingMode === "bundled" || monthlyPaise > 0) &&
    (pricingMode !== "separate" || commissionBps >= 0);

  const secretAlreadySet = configQuery.data?.razorpay_key_secret_set === true;
  const canSavePayment =
    paymentDirty &&
    !saveMutation.isPending &&
    (paymentOwner === "eduviaos" ||
      (centreKeyId.trim().length > 0 && (secretAlreadySet || centreKeySecret.trim().length > 0)));

  async function handleSave() {
    try {
      await saveMutation.mutateAsync(body);
      toast.success("Learner app pricing saved.");
      setDirty(false);
    } catch {
      toast.error("Couldn't save learner app pricing. Try again.");
    }
  }

  async function handleSavePayment() {
    if (!configQuery.data) return;
    const paymentBody: LearnerAppConfigUpdateRequest = {
      pricing_mode: configQuery.data.pricing_mode,
      monthly_price_paise: configQuery.data.monthly_price_paise,
      commission_bps: configQuery.data.commission_bps,
      payment_owner: paymentOwner,
    };
    if (paymentOwner === "centre") {
      paymentBody.razorpay_key_id = centreKeyId.trim();
      if (centreKeySecret.trim()) paymentBody.razorpay_key_secret = centreKeySecret.trim();
      if (centreWebhookSecret.trim()) {
        paymentBody.razorpay_webhook_secret = centreWebhookSecret.trim();
      }
    }
    try {
      await saveMutation.mutateAsync(paymentBody);
      toast.success("Payment collection saved.");
      setCentreKeySecret("");
      setCentreWebhookSecret("");
      setPaymentDirty(false);
    } catch {
      toast.error("Couldn't save payment collection. Try again.");
    }
  }

  if (configQuery.isLoading) {
    return (
      <Card>
        <CardContent className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading learner app settings…
        </CardContent>
      </Card>
    );
  }

  if (configQuery.isError) {
    return (
      <Card>
        <CardContent className="text-destructive py-8 text-sm">
          Couldn't load learner app settings for this tenant.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Learner app pricing</CardTitle>
          <CardDescription>
            How this study centre charges for the learner app. Separate pricing blocks learning
            routes until staff grant access after payment at the centre.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <RadioGroup
            value={pricingMode}
            onValueChange={(next) => {
              setPricingMode(next as LearnerAppPricingMode);
              setDirty(true);
            }}
            className="grid gap-3"
          >
            {PRICING_MODES.map((mode) => (
              <label
                key={mode.value}
                className={cn(
                  "border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3",
                  pricingMode === mode.value && "border-primary/40 bg-muted/30",
                )}
              >
                <RadioGroupItem value={mode.value} className="mt-0.5" />
                <div className="grid gap-1">
                  <span className="text-sm font-medium">{mode.label}</span>
                  <span className="text-muted-foreground text-xs">{mode.description}</span>
                </div>
              </label>
            ))}
          </RadioGroup>

          {pricingMode !== "bundled" ? (
            <div className="grid max-w-xs gap-2">
              <Label htmlFor="learner-app-monthly">Monthly price (₹)</Label>
              <Input
                id="learner-app-monthly"
                inputMode="decimal"
                placeholder="499"
                value={monthlyRupees}
                onChange={(e) => {
                  setMonthlyRupees(e.target.value);
                  setDirty(true);
                }}
              />
              {monthlyPaise > 0 ? (
                <p className="text-muted-foreground text-xs">
                  Learners see {formatLearnerAppPrice(monthlyPaise)} / month on the paywall.
                </p>
              ) : null}
            </div>
          ) : null}

          {pricingMode === "separate" ? (
            <div className="grid max-w-xs gap-2">
              <Label htmlFor="learner-app-commission">Platform commission (%)</Label>
              <Input
                id="learner-app-commission"
                inputMode="decimal"
                placeholder="15"
                value={commissionPct}
                onChange={(e) => {
                  setCommissionPct(e.target.value);
                  setDirty(true);
                }}
              />
              <p className="text-muted-foreground text-xs">
                Recorded when EduViaOS collects the fee. A centre using its own Razorpay keeps the
                full payment.
              </p>
            </div>
          ) : null}

          <div>
            {pricingMode !== "bundled" && monthlyPaise <= 0 ? (
              <p id="learner-app-save-hint" className="text-muted-foreground mb-2 text-xs">
                Enter a monthly price above ₹0 to save this pricing.
              </p>
            ) : null}
            <Button
              type="button"
              disabled={!canSave}
              aria-describedby={
                pricingMode !== "bundled" && monthlyPaise <= 0 ? "learner-app-save-hint" : undefined
              }
              onClick={() => void handleSave()}
            >
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-4" aria-hidden="true" />
              )}
              Save pricing
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Who collects app payments</CardTitle>
          <CardDescription>
            EduViaOS can charge learners with the platform Razorpay account, or this centre can use
            its own.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <RadioGroup
            value={paymentOwner}
            onValueChange={(next) => {
              setPaymentOwner(next as LearnerAppPaymentOwner);
              setPaymentDirty(true);
            }}
            className="grid gap-3"
          >
            <label className="border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="eduviaos" className="mt-0.5" />
              <div className="grid gap-1">
                <span className="text-sm font-medium">EduViaOS Razorpay</span>
                <span className="text-muted-foreground text-xs">
                  Learners pay the platform account. Replace that key from Billing.
                </span>
              </div>
            </label>
            <label className="border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="centre" className="mt-0.5" />
              <div className="grid gap-1">
                <span className="text-sm font-medium">This centre&apos;s Razorpay</span>
                <span className="text-muted-foreground text-xs">
                  Learners pay the centre&apos;s own Razorpay account.
                </span>
              </div>
            </label>
          </RadioGroup>
          {paymentOwner === "centre" ? (
            <div className="grid max-w-md gap-4">
              <div className="grid gap-2">
                <Label htmlFor="centre-razorpay-key">Razorpay Key ID</Label>
                <Input
                  id="centre-razorpay-key"
                  autoComplete="off"
                  placeholder="rzp_test_…"
                  value={centreKeyId}
                  onChange={(event) => {
                    setCentreKeyId(event.target.value);
                    setPaymentDirty(true);
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="centre-razorpay-secret">Key Secret</Label>
                <Input
                  id="centre-razorpay-secret"
                  type="password"
                  autoComplete="new-password"
                  placeholder={secretAlreadySet ? "Saved. Enter a new secret to replace it." : ""}
                  value={centreKeySecret}
                  onChange={(event) => {
                    setCentreKeySecret(event.target.value);
                    setPaymentDirty(true);
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="centre-razorpay-webhook">Webhook secret</Label>
                <Input
                  id="centre-razorpay-webhook"
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    configQuery.data?.razorpay_webhook_secret_set
                      ? "Saved. Enter a new secret to replace it."
                      : "Optional"
                  }
                  value={centreWebhookSecret}
                  onChange={(event) => {
                    setCentreWebhookSecret(event.target.value);
                    setPaymentDirty(true);
                  }}
                />
              </div>
            </div>
          ) : null}
          <Button type="button" disabled={!canSavePayment} onClick={() => void handleSavePayment()}>
            {saveMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="size-4" aria-hidden="true" />
            )}
            Save collection
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
